// frontend/src/features/livestock/photoInboxErrors.ts
// What to tell the user when sorting a photo fails (plan 4.4 and the 409
// details of section 9).

import {
  PhotoInboxBusyError,
  PhotoInboxConflictError,
  PhotoInboxError,
  PhotoInboxNetworkError,
} from '@/api/photoInbox';
import { RateLimitError } from '@/api/rateLimit';
import { isLivestockNotFound } from '@/hooks/usePhotoInbox';
import type { AssignTarget } from '@/types/photoInbox';

export interface SortFailure {
  /** A sentence: why it failed. */
  message: string;
  /** Sending the same request again could work. */
  canRetry: boolean;
}

export function isNotFound(error: unknown): boolean {
  return error instanceof PhotoInboxError && error.status === 404;
}

export function isConflict(error: unknown, code: string): boolean {
  return error instanceof PhotoInboxConflictError && error.conflict.code === code;
}

/** Why an assign or discard failed; null when the photo is no longer pending (sorted elsewhere). */
export function describeSortFailure(error: unknown): SortFailure | null {
  if (isLivestockNotFound(error)) {
    return { message: "That organism isn't in this tank any more.", canRetry: false };
  }
  if (isNotFound(error) || isConflict(error, 'not_pending')) return null;
  const elsewhere = assignedElsewhere(error);
  if (elsewhere) {
    return {
      message: `${describeAssignedElsewhere(elsewhere.target, elsewhere.livestock_name)}.`,
      canRetry: false,
    };
  }
  if (isConflict(error, 'in_progress')) {
    return {
      message: 'The previous assign of this photo is still finishing. Try again in a moment.',
      canRetry: true,
    };
  }
  if (error instanceof PhotoInboxBusyError) {
    return {
      message: 'The server is busy cropping photos. Try again in a moment.',
      canRetry: true,
    };
  }
  if (error instanceof PhotoInboxNetworkError) return { message: 'No connection.', canRetry: true };
  if (error instanceof RateLimitError) return { message: error.message, canRetry: true };
  if (error instanceof PhotoInboxError && error.status === 422) {
    if (/entry_date/.test(error.message)) {
      return { message: 'Pick a date between 1990 and now.', canRetry: false };
    }
    if (/entry_text/.test(error.message)) {
      return { message: "The note has a character that can't be saved.", canRetry: false };
    }
    return { message: error.message.replace(/^Value error, /, ''), canRetry: false };
  }
  if (error instanceof PhotoInboxError && error.status === 400) {
    return { message: error.message, canRetry: false };
  }
  const message = error instanceof Error && error.message ? error.message : 'Something went wrong.';
  return { message, canRetry: true };
}

/**
 * Where the photo went instead, when an assign was refused because it is on
 * the other target already (a tank shot, or an organism's timeline).
 */
export function assignedElsewhere(
  error: unknown
): { target: AssignTarget; livestock_name: string | null } | null {
  if (!(error instanceof PhotoInboxConflictError)) return null;
  const { conflict } = error;
  return conflict.code === 'assigned_elsewhere'
    ? { target: conflict.target, livestock_name: conflict.livestock_name }
    : null;
}

/** "Already a tank shot", or "Already on Green Acro". */
export function describeAssignedElsewhere(target: AssignTarget, name: string | null): string {
  return target === 'tank' ? 'Already a tank shot' : `Already on ${name ?? 'another organism'}`;
}

/**
 * Why an undo failed, in words; null when trying again could work. `target`
 * is where the undone assign went (null for a discard).
 */
export function describeUndoFailure(
  error: unknown,
  target: AssignTarget | null = null
): string | null {
  if (error instanceof PhotoInboxConflictError) {
    const { conflict } = error;
    if (conflict.code === 'on_livestock') {
      return conflict.target === 'tank'
        ? "Can't undo: the photo is a tank shot now."
        : `Can't undo: the photo is on ${conflict.livestock_name} now.`;
    }
    if (conflict.code === 'expired')
      return 'Too late to undo: photos can be undone for 15 minutes.';
    if (conflict.code === 'uploaded_again') {
      return "Can't undo: this photo has been uploaded again. Sort that copy instead.";
    }
    if (conflict.code === 'gone') {
      return target === 'tank'
        ? "Can't undo: the tank shot has been deleted."
        : "Can't undo: the timeline entry has been deleted.";
    }
    if (conflict.code === 'in_progress') return null;
    return conflict.message;
  }
  if (isNotFound(error)) return "This photo can't be brought back any more.";
  return null;
}
